import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from '../config/supabase';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState('student'); // 'admin' o 'student'

  useEffect(() => {
    // Verificar sesión al montar
    checkUser();

    // Escuchar cambios de autenticación
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session) {
        setUser(session.user);
        // Obtener rol del usuario
        const userRole = await getUserRole(session.user.id);
        setRole(userRole);
      } else {
        setUser(null);
        setRole('student');
      }
      setLoading(false);
    });

    return () => subscription?.unsubscribe();
  }, []);

  const checkUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUser(user);
        const userRole = await getUserRole(user.id);
        setRole(userRole);
      }
    } catch (error) {
      console.error('Error verificando usuario:', error);
    } finally {
      setLoading(false);
    }
  };

  const getUserRole = async (userId) => {
    try {
      const { data } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', userId)
        .single();
      return data?.role || 'student';
    } catch (error) {
      return 'student';
    }
  };

  const signup = async (email, password, fullName) => {
    try {
      const { data: { user }, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { full_name: fullName },
        },
      });

      if (error) throw error;

      // Crear perfil del usuario
      if (user) {
        await supabase.from('user_profiles').insert({
          id: user.id,
          email: user.email,
          full_name: fullName,
          role: 'student',
        });
      }

      return user;
    } catch (error) {
      throw error;
    }
  };

  const login = async (email, password) => {
    try {
      const { data: { user }, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      setUser(user);
      const userRole = await getUserRole(user.id);
      setRole(userRole);

      return user;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setRole('student');
    } catch (error) {
      throw error;
    }
  };

  const isAdmin = role === 'admin';
  const isAuthenticated = user !== null;

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        role,
        isAdmin,
        isAuthenticated,
        signup,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de AuthProvider');
  }
  return context;
};
